import $ from 'jquery';
import _ from 'underscore';
import Immutable from 'immutable';

import ErrorData from 'sharedreact/data/ErrorData';
import Encode from 'sharedreact/utils/Encode';
import Progress from 'sharedreact/data/Progress';
import FileSupport from 'sharedreact/utils/FileSupport';
import LogEntry from 'sharedreact/data/LogEntry';
import StringSupport from 'sharedreact/utils/StringSupport';

import type CancelHandle from 'sharedreact/utils/CancelHandle';
import Url from 'sharedreact/data/Url';

type OnSuccess = (data: any) => void;
type OnError = (error: ErrorData) => void;

const _protocol: string = window.location.protocol;
const _server: string = window.location.hostname;
const _port: string = window.location.port;
let _unauthorizedHandler: () => void;
let _unauthorizedHandlerWithPath: (servicePath: string) => void;

let _servicesSubFolder: string = '';

let _uploadProgressCallback: ((progress: Progress) => void) | null = null;
let _setCancelHandle: ((progresId: string, callback: CancelHandle) => void) | null = null;

let _logger: ((logEntry: LogEntry) => void) | null = null;

let _lastRequestSentTime = 0;

// private functions

function _handleUnauthorized (servicePath: string, loggerData: {[key: string]: any} | null) {
  if (_logger != null && loggerData != null) {
    let dataToLog: {[key: string]: any} = loggerData;
    dataToLog.endTime = new Date().getTime();
    dataToLog.status = 'failed';
    dataToLog.error = new ErrorData({
      message: 'unauthorized'
    });
    const elapsedTime: number = dataToLog.endTime - dataToLog.startTime;
    _logger(new LogEntry({
      time: new Date(),
      logLevel: LogEntry.LogLevel.trace,
      type: dataToLog.callType,
      message: StringSupport.replaceArgs('{0} call "{1}" failed ({2} ms).', dataToLog.callType, dataToLog.name, elapsedTime + ''),
      details: dataToLog
    }));
  }

  if (_unauthorizedHandler != null) {
    _unauthorizedHandler();
  }
  if (_unauthorizedHandlerWithPath != null) {
    _unauthorizedHandlerWithPath(servicePath);
  }
}

function _handleError (onError: OnError, errorData: ErrorData, loggerData: {[key: string]: any} | null) {
  if (_logger != null && loggerData != null) {
    const time: Date = new Date();

    let dataToLog: {[key: string]: any} = loggerData;
    dataToLog.endTime = time.getTime();
    dataToLog.status = 'failed';
    dataToLog.error = errorData;
    const elapsedTime: number = dataToLog.endTime - dataToLog.startTime;
    _logger(new LogEntry({
      time: time,
      logLevel: LogEntry.LogLevel.trace,
      type: dataToLog.callType,
      message: StringSupport.replaceArgs('{0} call "{1}" failed ({2} ms).', dataToLog.callType, dataToLog.name, elapsedTime + ''),
      details: dataToLog
    }));
  }

  onError(errorData);
}

function handleSuccess (result: any, onSuccess: OnSuccess, onError: OnError, loggerData?: {[key: string]: any} | null): void {
  if (result.status === 'ok') {
    if (onSuccess != null) {
      if (_logger != null && loggerData != null) {
        const time: Date = new Date();

        let dataToLog: {[key: string]: any} = loggerData;
        dataToLog.endTime = new Date().getTime();
        dataToLog.status = 'succeeded';
        dataToLog.result = result;
        const elapsedTime: number = dataToLog.endTime - dataToLog.startTime;
        _logger(new LogEntry({
          time: time,
          logLevel: LogEntry.LogLevel.trace,
          type: dataToLog.callType,
          message: StringSupport.replaceArgs('{0} call "{1}" succeeded ({2} ms).', dataToLog.callType, dataToLog.name, elapsedTime + ''),
          details: dataToLog
        }));
      }
      onSuccess(result.data);
    }
  } else {
    if (onError != null) {
      const errorData: ErrorData = ErrorData.fromJS(result);

      _handleError(onError, errorData, loggerData ?? null);
    }
  }
}

function handleError (
  jqXHR: JQuery.jqXHR<any>,
  textStatus: JQuery.Ajax.ErrorTextStatus,
  errorThrown: string,
  servicePath: string,
  onError: OnError,
  loggerData: {[key: string]: any} | null
): void {
  if (onError != null) {
    if (jqXHR.status === 403) {
      if (jqXHR.statusText === 'NO LICENSE') {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.general.nolicenseerror',
          details: JSON.stringify({
            status: jqXHR.status,
            textStatus: jqXHR.statusText,
            servicePath: servicePath
          }, null, 2),
          validationPath: Immutable.List()
        });

        _handleError(onError, errorData, loggerData);
      } else {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.general.noaccessright',
          details: JSON.stringify({
            status: jqXHR.status,
            textStatus: jqXHR.statusText,
            servicePath: servicePath
          }, null, 2),
          validationPath: Immutable.List()
        });

        _handleError(onError, errorData, loggerData);
      }
    } else {
      const errorData: ErrorData = new ErrorData().merge({
        message: (textStatus === 'parsererror' ? 'shared.general.parsingerror' : 'shared.general.servererror'),
        details: JSON.stringify({
          status: jqXHR.status,
          textStatus: jqXHR.statusText,
          servicePath: servicePath
        }, null, 2),
        validationPath: jqXHR.status === 401 ? Immutable.List(['dummy-to-prevent-error-message']) : Immutable.List()
      });

      _handleError(onError, errorData, loggerData);
    }
  }

  if (jqXHR.status === 401) {
    _handleUnauthorized(servicePath, loggerData);
  }
}

function _getServicePath (servicename: string, useServicesSubFolder: boolean): string {
  if (_port === '') {
    if (useServicesSubFolder) {
      return _protocol + '//' + _server + '/' + _servicesSubFolder + servicename;
    } else {
      return _protocol + '//' + _server + '/' + servicename;
    }
  } else {
    if (useServicesSubFolder) {
      return _protocol + '//' + _server + ':' + _port + '/' + _servicesSubFolder + servicename;
    } else {
      return _protocol + '//' + _server + ':' + _port + '/' + servicename;
    }
  }
}

// exported functions

function setLogger (logger: (logEntry: LogEntry) => void): void {
  _logger = logger;
}

function setServicesSubFolder (subFolder: string): void {
  _servicesSubFolder = subFolder;
}

function get (
  servicename: string,
  data: {[key: string]: any} | null,
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean = true
): void {
  const servicePath = _getServicePath(servicename, useServicesSubFolder);

  let loggerData: {[key: string]: any} | null = null;
  if (_logger != null) {
    loggerData = {
      callType: 'get',
      name: servicename,
      path: servicePath,
      parameters: data,
      startTime: new Date().getTime(),
      endTime: 0,
      status: 'unknown',
      error: null
    };
  }

  if (data != null) {
    const request = $.ajax(servicePath, { dataType: 'json', data: data })
      .fail((jqXHR, textStatus, errorThrown) => handleError(jqXHR, textStatus, errorThrown, servicePath, onError, loggerData));

    _lastRequestSentTime = new Date().getTime();

    if (request != null) {
      request.done((result) => handleSuccess(result, onSuccess, onError, loggerData));
    }
  } else {
    const request = $.ajax(servicePath, { dataType: 'json' })
      .fail((jqXHR, textStatus, errorThrown) => handleError(jqXHR, textStatus, errorThrown, servicePath, onError, loggerData));

    if (request != null) {
      request.done((result) => handleSuccess(result, onSuccess, onError, loggerData));
    }
  }
}

function post (
  servicename: string,
  data: {[key: string]: any} | null,
  onSuccess: OnSuccess,
  onError: OnError, useServicesSubFolder: boolean = true
): void {
  _post(servicename, data, onSuccess, onError, useServicesSubFolder, true);
}

function _post (
  servicename: string,
  data: {[key: string]: any} | null,
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean,
  updateLastSentTime: boolean
): void {
  const servicePath = _getServicePath(servicename, useServicesSubFolder);

  let loggerData: {[key: string]: any} | null = null;
  if (_logger != null) {
    loggerData = {
      callType: 'post',
      name: servicename,
      path: servicePath,
      parameters: data,
      startTime: new Date().getTime(),
      endTime: 0,
      status: 'unknown',
      error: null,
      result: null
    };
  }

  if (data != null) {
    const request = $.ajax(servicePath, {
      dataType: 'json',
      data: JSON.stringify(data),
      type: 'post',
      contentType: 'application/json'
    }).fail((jqXHR, textStatus, errorThrown) => handleError(jqXHR, textStatus, errorThrown, servicePath, onError, loggerData));

    if (updateLastSentTime) {
      _lastRequestSentTime = new Date().getTime();
    }

    if (request != null) {
      request.done((result) => handleSuccess(result, onSuccess, onError, loggerData));
    }
  } else {
    const request = $.ajax(servicePath, {
      dataType: 'json',
      type: 'post',
      contentType: 'application/json'
    }).fail((jqXHR, textStatus, errorThrown) => handleError(jqXHR, textStatus, errorThrown, servicePath, onError, loggerData));

    if (updateLastSentTime) {
      _lastRequestSentTime = new Date().getTime();
    }

    if (request != null) {
      request.done((result) => handleSuccess(result, onSuccess, onError, loggerData));
    }
  }
}

// Upload Chunked: based on code from https://api.video/blog/tutorials/uploading-large-files-with-javascript

const CHUNK_SIZE = 1024 * 1024 * 30;
const CHUNK_STRETCH = 1024 * 1024;

function _uploadChunkForm (
  servicePath: string,
  headerData: {[key: string]: any} | null,
  file: File,
  data: {[key: string]: any},
  fileSpecificData: {[key: string]: any},
  chunkForm: FormData,
  start: number,
  end: number,
  totalSize: number,
  onSuccess: OnSuccess,
  onError: OnError,
  loggerData: {[key: string]: any} | null
): void {
  const blobEnd: number = end - 1;
  const contentRange: string = 'bytes ' + start + '-' + blobEnd + '/' + totalSize;

  let request = new XMLHttpRequest();
  request.open('POST', servicePath, true);
  request.overrideMimeType('text/plain; charset=UTF-8');
  request.setRequestHeader('Content-Range', contentRange);

  if (headerData != null) {
    for (const headerKey in headerData) {
      const headerValue: string = headerData[headerKey];
      request.setRequestHeader(headerKey, headerValue);
    }
  }

  request.onload = () => {
    if (request.status === 200) {
      if (onSuccess != null) {
        // if last response
        const responseData: any = JSON.parse(request.responseText);
        if (responseData.status !== 'ok' || responseData.data.status !== 'PART_UPLOADED') {
          handleSuccess(responseData, onSuccess, onError);
        } else if (end < totalSize) {
          _uploadChunk(servicePath, headerData, file, end, responseData.data.serverId, data, fileSpecificData, onSuccess, onError);
        } else {
          const errorData: ErrorData = new ErrorData().merge({
            message: 'unable to send files',
            details: 'no more parts'
          });

          _handleError(onError, errorData, loggerData);
        }
      }
    } else {
      if (request.status === 401) {
        _handleUnauthorized(servicePath, loggerData);
      } else if (request.status === 403) {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.general.nouploadaccessright',
          details: JSON.stringify({
            status: request.status,
            textStatus: request.statusText,
            servicePath: servicePath
          }, null, 2),
          validationPath: Immutable.List()
        });
        
        _handleError(onError, errorData, loggerData);
      } else {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'unable to send files',
          details: 'status=' + request.status
        });

        _handleError(onError, errorData, loggerData);
      }
    }
  };

  request.send(chunkForm);

  _lastRequestSentTime = new Date().getTime();
}

function _uploadChunk (
  servicePath: string,
  headerData: {[key: string]: any} | null,
  file: File,
  start: number,
  serverId: string,
  data: {[key: string]: any},
  fileSpecificData: {[key: string]: any},
  onSuccess: OnSuccess,
  onError: OnError,
  loggerData?: {[key: string]: any} | null): void {
  const totalSize: number = file.size;

  const filename: string = file.name.normalize();

  let chunkEnd: number = Math.min(start + CHUNK_SIZE, totalSize);
  // if chunk end is very close to end of file,
  // make chunk larger to prevent sending a very small chunk
  if (chunkEnd + CHUNK_STRETCH >= totalSize) {
    chunkEnd = totalSize;
  }

  const chunk: Blob = file.slice(start, chunkEnd);

  let chunkForm: FormData = new FormData();

  for (const dataKey in data) {
    chunkForm.append(dataKey, JSON.stringify(data[dataKey]));
  }

  const fileId: string = _.uniqueId();

  chunkForm.append('fileId', fileId);
  chunkForm.append('file', chunk, fileId);
  chunkForm.append(fileId, JSON.stringify(filename));

  if (fileSpecificData != null) {
    chunkForm.append(fileId + '_data', JSON.stringify(fileSpecificData));
  }

  if (serverId !== '') {
    // serverId is the id returned by server to identify the file,
    // will be used on server side to identify the chunks to concatinate
    chunkForm.append('serverId', serverId);
  }

  _uploadChunkForm(servicePath, headerData, file, data, fileSpecificData, chunkForm, start, chunkEnd, totalSize, onSuccess, onError, loggerData ?? null);
}

function _uploadChunked (
  servicename: string,
  headerData: {[key: string]: any} | null,
  files: FileList | File[],
  data: {[key: string]: any},
  fileSpecificData: {[key: string]: any}[],
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean,
  loggerData: {[key: string]: any} | null
): void {
  const servicePath: string = _getServicePath(servicename, useServicesSubFolder);

  for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
    const currentFile: File = files[ fileIndex ];
    const currentFileSpecificData: {[key: string]: any} = fileSpecificData.length > fileIndex ? fileSpecificData[fileIndex] || {} : {};

    _uploadChunk(servicePath, headerData, currentFile, 0, '', data, currentFileSpecificData, onSuccess, onError);
  }
}

function _upload (
  servicename: string,
  headerData: {[key: string]: any} | null,
  files: FileList | File[],
  data: {[key: string]: any},
  fileSpecificData: {[key: string]: any}[],
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean,
  progressId: string | undefined,
  progressType: string | null,
  loggerData: {[key: string]: any} | null
): void {
  const servicePath: string = _getServicePath(servicename, useServicesSubFolder);

  let formData: FormData = new FormData();

  for (const dataKey in data) {
    formData.append(dataKey, JSON.stringify(data[ dataKey ]));
  }

  let fileNamesArray: string[] = [];
  if (files.length > 0) {
    for (let fileIndex: number = 0; fileIndex < files.length; fileIndex++) {
      const currentFile = files[ fileIndex ];

      const filename = currentFile.name.normalize();

      fileNamesArray.push(filename);

      const fileId: string = _.uniqueId();

    // use id for filename and save actual filename in properties, because filename does not handle UTF-8 correctly
      formData.append('files', currentFile, fileId);
      formData.append(fileId, JSON.stringify(filename));

      if (fileIndex < fileSpecificData.length) {
        const fileData: {[key: string]: any} = fileSpecificData[fileIndex];
        formData.append(fileId + '_data', JSON.stringify(fileData));
      }
    }
  } else if (Array.isArray(data.files)) {
    for (let fileIndex: number = 0; fileIndex < data.files.length; fileIndex++) {
      fileNamesArray.push(data.files[fileIndex].filename);
    }
  }

  let request: XMLHttpRequest = new XMLHttpRequest();

  const _progressId: string = progressId || _.uniqueId();

  const filesString: string = fileNamesArray.join(', ');

  if (progressType != null) {
    request.upload.addEventListener('progress', (event: ProgressEvent) => {
      if (event.lengthComputable) {
        let percentComplete: number = (event.loaded / event.total) * 100;

        if (Math.round(percentComplete) < 100) {
          if (_uploadProgressCallback != null) {
            _uploadProgressCallback(new Progress({id: _progressId, type: progressType || '', description: filesString, progress: percentComplete}));
          }
        }
      }
    });
  }

  request.open('POST', servicePath, true);
  request.overrideMimeType('text/plain; charset=UTF-8');

  if (headerData != null) {
    for (const headerKey in headerData) {
      const headerValue: string = headerData[headerKey];
      request.setRequestHeader(headerKey, headerValue);
    }
  }

  request.onload = function () {
    if (request.status === 200) {
      if (onSuccess != null) {
        let responseData: any = JSON.parse(request.responseText);

        if (responseData.status === 'ok' && responseData.data.status === 'FILES_EXIST') {
          responseData.data.progressId = _progressId;
        } else {
          if (progressType != null && _uploadProgressCallback != null) {
            _uploadProgressCallback(new Progress({id: _progressId, type: progressType || '', description: filesString, progress: 100}));
          }
        }

        handleSuccess(responseData, onSuccess, onError, loggerData);
      }
    } else {
      if (progressType != null && _uploadProgressCallback != null) {
        _uploadProgressCallback(new Progress({id: _progressId, type: progressType || '', description: filesString, progress: 100}));
      }

      if (request.status === 401) {
        _handleUnauthorized(servicePath, loggerData);
      } else if (request.status === 403) {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.general.nouploadaccessright',
          details: JSON.stringify({
            status: request.status,
            textStatus: request.statusText,
            servicePath: servicePath
          }, null, 2),
          validationPath: Immutable.List()
        });

        _handleError(onError, errorData, loggerData);
      } else {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.upload.unabletoupload',
          details: 'status=' + request.status
        });

        _handleError(onError, errorData, loggerData);
      }
    }
  };

  request.send(formData);
  _lastRequestSentTime = new Date().getTime();

  if (_setCancelHandle != null) {
    _setCancelHandle(_progressId, { cancel: () => request.abort() });
  }
}

const MAX_FILE_SIZE: number = 4 * 1024 * 1024 * 1024; // should be the same as on server side!

function upload (
  servicename: string,
  files: FileList | File[],
  data: {[key: string]: any},
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean = true,
  enableUploadInChunks: boolean = false,
  progressId: string | undefined = undefined,
  progressType: string | undefined = 'upload'
): void {
  uploadWithHeaderData(
    servicename,
    null,
    files,
    data,
    [],
    onSuccess,
    onError,
    useServicesSubFolder,
    enableUploadInChunks,
    progressId,
    progressType
  );
}

function uploadWithHeaderData (
  servicename: string,
  headerData: {[key: string]: any} | null,
  files: FileList | File[],
  data: {[key: string]: any},
  fileSpecificData: {[key: string]: any}[],
  onSuccess: OnSuccess,
  onError: OnError,
  useServicesSubFolder: boolean = true,
  enableUploadInChunks: boolean = false,
  progressId: string | undefined = undefined,
  progressType: string | undefined = 'upload'
): void {
  const servicePath = _getServicePath(servicename, useServicesSubFolder);

  let loggerData: {[key: string]: any} | null = null;
  if (_logger != null) {
    loggerData = {
      callType: 'upload',
      name: servicename,
      path: servicePath,
      parameters: data,
      startTime: new Date().getTime(),
      endTime: 0,
      status: 'unknown',
      error: null,
      result: null
    };
  }

  if (enableUploadInChunks) {
    // check the size of the files, upload small files together (but keep upload order) and upload large files in chunks
    let filesToUploadTogether: File[] = [];

    let totalFileSize: number = 0;
    for (let fileIndex: number = 0; fileIndex < files.length; fileIndex++) {
      const currentFile: File = files[ fileIndex ];

      const fileSize: number = currentFile.size;
      totalFileSize += fileSize;

      if (totalFileSize < CHUNK_SIZE + CHUNK_STRETCH) {
        filesToUploadTogether.push(currentFile);
      } else {
        if (filesToUploadTogether.length > 0) {
          _upload(servicename, headerData, filesToUploadTogether, data, fileSpecificData, onSuccess, onError, useServicesSubFolder, progressId, progressType, loggerData);
          filesToUploadTogether = [];
          totalFileSize = 0;
        }

        if (fileSize < CHUNK_SIZE + CHUNK_STRETCH) {
          filesToUploadTogether.push(currentFile);
          totalFileSize += fileSize;
        } else {
          _uploadChunked(servicename, headerData, [currentFile], data, fileSpecificData, onSuccess, onError, useServicesSubFolder, loggerData);
        }
      }
    }

    if (filesToUploadTogether.length > 0) {
      _upload(servicename, headerData, filesToUploadTogether, data, fileSpecificData, onSuccess, onError, useServicesSubFolder, progressId, progressType, loggerData);
    }
  } else {
    for (let fileIndex: number = 0; fileIndex < files.length; fileIndex++) {
      const currentFile: File = files[ fileIndex ];

      const fileSize: number = currentFile.size;

      if (fileSize > MAX_FILE_SIZE) {
        const errorData: ErrorData = new ErrorData().merge({
          message: 'shared.upload.filetoobig',
          messageParams: Immutable.List([FileSupport.formatFileSize(MAX_FILE_SIZE)])
        });

        _handleError(onError, errorData, loggerData);
        return;
      }
    }

    _upload(servicename, headerData, files, data, fileSpecificData, onSuccess, onError, useServicesSubFolder, progressId, progressType, loggerData);
  }
}

function getUrl (
  servicename: string,
  data: {[key: string]: any},
  useServicesSubFolder: boolean = true
): string {
  let url = _getServicePath(servicename, useServicesSubFolder);
  let queryData = '?';
  for (const dataKey in data) {
    if (queryData.length > 1) {
      queryData += '&';
    }
    queryData += dataKey + '=' + encodeURIComponent(data[dataKey]);
  }
  if (queryData.length > 1) {
    url += queryData;
  }

  return Encode.normalizeHexChars(url);
}

function prepareDownload (
  servicename: string,
  urls: string,
  useServicesSubFolder: boolean = true
  ) : Promise<Url> {
  const servicePath = _getServicePath(servicename, useServicesSubFolder);

  let loggerData: {[key: string]: any} | null = null;
  if (_logger != null) {
    loggerData = {
      callType: 'post',
      name: servicename,
      path: servicePath,
      parameters: urls,
      startTime: new Date().getTime(),
      endTime: 0,
      status: 'unknown',
      error: null,
      result: null
    };
  }

 const payload = {url: Encode.normalizeHexChars(urls)};

  return new Promise((onSuccess, onError) => { 
    const request = $.ajax(servicePath, {
      dataType: 'json',
      data: JSON.stringify(payload),
      type: 'post',
      contentType: 'application/json'
    })
      .then(result => {
        const url = Url.fromJS(result.data);
        onSuccess(url);
        }) 
      .catch(error => onError(error)); 
  }); 
} 

function createWebAppUrl (webAppPath: string): string {
  const path = window.location.href.replace(/.*:\/\/[^/]*/, '');

  const _webAppPath = webAppPath.replace(/\/?$/, '/');

  if (path.startsWith('/#') || path.startsWith('/workspaces/#')) {
    return window.location.href.replace(/\/#.*/, _webAppPath);
  } else {
    return window.location.href.replace(/\/[^/]*\/#.*/, _webAppPath);
  }
}

function setUnauthorizedHandler (handler: () => void): void {
  _unauthorizedHandler = handler;
}

function setUnauthorizedHandlerWithPath (handler: (path: string) => void): void {
  _unauthorizedHandlerWithPath = handler;
}

function createOpenUrl (fileUrl:string, sessionId: string, language: string | null): string {
  let openUrl: string = fileUrl.replace('file://', 'eskoedit://');
  if (serverIsInTheDatacenter()) {
    openUrl = openUrl.concat('?tnl=true&download=true');

    const serverName = window.location.hostname;
    openUrl = openUrl.concat('&publicAEHost=' + serverName);

    openUrl = openUrl.concat('&sessionid=' + sessionId);
  }

  if (language != null && language !== '') {
    openUrl = openUrl.concat((openUrl.includes('?') ? '&' : '?') + 'language=' + _getAppLauncherLanguageCode(language));
  }

  return openUrl;
}

function _getAppLauncherLanguageCode (language:string): string {
  // supported languages: en, de, fr, it, es, ja, ko, zh-TW, zh-CN
   switch (language) {
     case 'zh': {
      return 'zh-CN';
    }
    default: {
      return language;
    }
  }
}

function _getDocsLanguageCode (language:string): string {
  switch (language) {
    case 'de': {
      return 'de-DE';
    }
    case 'fr': {
      return 'fr-FR';
    }
    case 'it': {
      return 'it-IT';
    }
    case 'es': {
      return 'es-ES';
    }
    case 'ja': {
      return 'ja-JP';
    }
    case 'zh': {
      return 'zh-CN';
    }
    case 'zh-TW': {
      return 'zh-TW';
    }
    case 'ko': {
      return 'ko-KO';
    }
    case 'en':
    default: {
      return 'en-US';
    }
  }
}

function createDocumentationLink (language: string, version: string, contextId: string): string {
  const languageCode: string = _getDocsLanguageCode(language);
  return `https://docs.esko.com/docs/${languageCode}/automationengine/${version}/userguide/home.html?q=&contextid=${contextId}`;
}

function serverIsInTheDatacenter (): boolean {
  const path = window.location.href.replace(/.[^/]*:\/\/[^/]*/, '');

  // If using the tunnel, there is an extra 'workspaces' layer added to the path
  // We assume that when using the tunnel, the server is located in the datacenter
  return path.startsWith('/workspaces/');
}

function setUploadProgressCallback (
  callback: ((progress: Progress) => void) | null,
  setCancelHandle?: (progresId: string, callback: CancelHandle) => void
): void {
  _uploadProgressCallback = callback;
  _setCancelHandle = setCancelHandle ?? null;
}

function getLastRequestSentTime (): number {
  return _lastRequestSentTime;
}

function sendHeartbeat (path: string | null): void {
  _post(
    (path != null ? path : 'services/heartbeat'),
    {},
    () => {},
    console.log,
    false,
    false
  );
}

function isSessionStillValid (
  onSuccess: () => void,
  onError: (errorData: ErrorData) => void,
  path: string | null
): void {
  _post(
    (path != null ? path : 'services/isSessionStillValid'),
    {},
    onSuccess,
    onError,
    false,
    false
  );
}

function createPreviewUrl (preview: string | null): string | null {
  if (preview != null) {
    let prefix = '../services/previews/';
    if (window.location.pathname.indexOf("/viewer/") != -1) {
      prefix = "../viewerservices/previews/";
    }
    if (serverIsInTheDatacenter()) {
      prefix = '../' + prefix;
    }
    return prefix + preview;
  }

  return null;
}


const ConnectionSupport = {
  setLogger,
  setServicesSubFolder,
  get,
  post,
  upload,
  uploadWithHeaderData,
  getUrl,
  createWebAppUrl,
  setUnauthorizedHandler,
  setUnauthorizedHandlerWithPath,
  createOpenUrl,
  createDocumentationLink,
  serverIsInTheDatacenter,
  setUploadProgressCallback,
  getLastRequestSentTime,
  sendHeartbeat,
  isSessionStillValid,
  createPreviewUrl,
  prepareDownload
};

export default ConnectionSupport;
